import '../../styles/mandant.css';
import './lohn.css';
import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, errorText } from '../../api';
import { LegalLinks } from '../../components/LegalLinks';
import { LangSwitch, t } from '../../i18n';
import { fmtDate, monthName } from '../../lib/format';
import { useApi } from '../../lib/useApi';

interface ShareInfo {
  firm: string;
  kind: 'verdienstabrechnung';
  year: number | null;
  month: number | null;
  expiresAt: string;
}

// Seite für den Mitarbeiter (ohne Anmeldung): Er öffnet den Link aus der E-Mail und lädt seine Verdienstabrechnung mit
// seinem Geburtsdatum herunter (server/src/modules/lohn/dokument-routes.ts). Nach 5 falschen Eingaben ist der Link
// gesperrt. Oben wählt er seine Sprache.
export function DokumentPage() {
  const { token = '' } = useParams();
  const info = useApi<ShareInfo>(`/api/public/dokument/${encodeURIComponent(token)}`);

  useEffect(() => {
    document.title = t('Ihre Datei');
  }, []);

  return (
    <div className="mapp">
      <div className="regwrap angaben">
        <div className="in">
          <div className="brandrow">
            <span className="logo wm" role="img" aria-label="Elbcons" />
            <span style={{ marginInlineStart: 'auto' }}>
              <LangSwitch />
            </span>
          </div>
          {info.loading ? (
            <div className="spinner" aria-label={t('Lädt')} />
          ) : info.error || !info.data ? (
            <>
              <h1>{t('Link ungültig')}</h1>
              <div className="card gone" role="alert">
                {info.error ?? t('Dieser Link ist ungültig.')}
              </div>
            </>
          ) : (
            <DokumentForm token={token} info={info.data} />
          )}
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}

function DokumentForm({ token, info }: { token: string; info: ShareInfo }) {
  const [geburtsdatum, setGeburtsdatum] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<{ url: string; name: string } | null>(null);
  const title =
    info.year && info.month
      ? t('Verdienstabrechnung {month}', { month: `${monthName(info.month)} ${info.year}` })
      : t('Verdienstabrechnung');

  useEffect(() => () => void (file && URL.revokeObjectURL(file.url)), [file]);

  async function open(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!geburtsdatum) {
      setError(t('Bitte geben Sie Ihr Geburtsdatum ein.'));
      return;
    }
    setBusy(true);
    try {
      const blob = await api.blob(`/api/public/dokument/${encodeURIComponent(token)}`, { geburtsdatum });
      const name = `${title}.pdf`;
      const next = { url: URL.createObjectURL(blob), name };
      setFile(next);
      download(next);
    } catch (err) {
      setError(errorText(err));
      if (err instanceof ApiError && (err.code === 'locked' || err.code === 'link_gone')) setLocked(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>{title}</h1>
      <p className="lead">
        {t('{firm} hat Ihnen diese Datei geschickt. Zum Öffnen geben Sie bitte Ihr Geburtsdatum ein.', { firm: info.firm })}
      </p>
      {file ? (
        <div className="card dlok">
          <p>{t('Die Datei wird heruntergeladen. Sie finden sie bei Ihren Downloads.')}</p>
          <button type="button" className="btn2 dark" onClick={() => download(file)}>
            {t('Noch einmal herunterladen')}
          </button>
        </div>
      ) : (
        <form className="form maform" onSubmit={open} noValidate>
          <div className="fld">
            <label htmlFor="geburtsdatum">
              <span>{t('Ihr Geburtsdatum')}</span>
              <input
                id="geburtsdatum"
                type="date"
                value={geburtsdatum}
                onChange={(e) => setGeburtsdatum(e.target.value)}
                disabled={locked}
                autoComplete="bday"
                required
              />
            </label>
          </div>
          {error && (
            <p className="formerr" role="alert">
              {error}
            </p>
          )}
          <button className="btn2 dark" type="submit" disabled={busy || locked}>
            {busy ? t('Einen Moment …') : t('Datei öffnen')}
          </button>
        </form>
      )}
      <p className="dlhint">
        {t('Der Link gilt bis zum {date}. Die Datei geht über eine verschlüsselte Verbindung an Sie.', {
          date: fmtDate(info.expiresAt),
        })}
      </p>
    </>
  );
}

function download(f: { url: string; name: string }) {
  const a = document.createElement('a');
  a.href = f.url;
  a.download = f.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
